import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// StoryBrand "The Plan" — 3 steps that make it easy for the customer to say yes
// Each panel = one step that removes fear of taking action

const Protocol = () => {
    const containerRef = useRef(null);

    useEffect(() => {
        let ctx = gsap.context(() => {
            const panels = gsap.utils.toArray('.protocol-panel');

            panels.forEach((panel, i) => {
                ScrollTrigger.create({
                    trigger: panel,
                    start: "top top",
                    pin: true,
                    pinSpacing: false,
                    end: () => "+=" + (panels.length - i - 1) * window.innerHeight
                });

                if (i < panels.length - 1) {
                    gsap.to(panel, {
                        scale: 0.9,
                        opacity: 0.3,
                        filter: "blur(20px)",
                        scrollTrigger: {
                            trigger: panels[i + 1],
                            start: "top bottom",
                            end: "top top",
                            scrub: true
                        }
                    });
                }
            });

        }, containerRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={containerRef} className="bg-[#0A0A14] relative">

            {/* Panel 1 — Step 1: We Build Your Machine */}
            <div className="protocol-panel h-[100dvh] w-full flex items-center justify-center p-6 relative bg-[#12121A] z-10 border-t border-[#F0EFF4]/5">
                <div className="max-w-4xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="font-data text-[#7B61FF] mb-4 text-sm tracking-widest uppercase">Step 01</div>
                        <h2 className="text-[#F0EFF4] font-bold text-5xl md:text-6xl mb-6">We Build<br /><span className="font-drama text-[#7B61FF] italic pr-2">Your Machine.</span></h2>
                        <p className="text-[#F0EFF4]/60 text-lg leading-relaxed mb-4">
                            We purchase a brand-new Apple Mac Mini or MacBook Air from the Apple Store and configure it entirely from scratch at our Kelowna Innovation Center — in tandem with our own internal agent swarm.
                        </p>
                        <p className="text-[#F0EFF4]/40 text-sm font-data">
                            Pristine Apple silicon. No refurbished parts. No ghost installs.
                        </p>
                    </div>
                    <div className="flex justify-center flex-col items-center">
                        <div className="relative w-64 h-64 flex items-center justify-center">
                            <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_20s_linear_infinite] absolute opacity-30">
                                <circle cx="50" cy="50" r="48" fill="none" stroke="#7B61FF" strokeWidth="0.5" strokeDasharray="4 8" />
                                <circle cx="50" cy="50" r="38" fill="none" stroke="#7B61FF" strokeWidth="0.5" strokeDasharray="2 4" />
                            </svg>
                            <svg viewBox="0 0 100 100" className="w-48 h-48 animate-[spin_15s_linear_infinite_reverse] relative z-10">
                                <rect x="25" y="25" width="50" height="50" fill="none" stroke="#7B61FF" strokeWidth="1" transform="rotate(45 50 50)" />
                                <rect x="25" y="25" width="50" height="50" fill="none" stroke="#F0EFF4" strokeWidth="0.5" transform="rotate(22.5 50 50)" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Panel 2 — Step 2: We Harden the Perimeter */}
            <div className="protocol-panel h-[100dvh] w-full flex items-center justify-center p-6 relative bg-[#181822] z-20 border-t border-[#F0EFF4]/5">
                <div className="max-w-4xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div className="order-2 md:order-1 flex justify-center flex-col items-center">
                        <div className="relative w-64 h-64 border border-[#7B61FF]/30 bg-[#0A0A14] flex items-center justify-center overflow-hidden rounded-2xl">
                            <div className="absolute inset-0 opacity-10 z-0" style={{ backgroundImage: 'radial-gradient(#7B61FF 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                            <div className="absolute top-0 left-0 w-full h-[2px] shadow-[0_4px_10px_#7B61FF] bg-[#7B61FF] animate-[translateY_3s_ease-in-out_infinite_alternate] z-10" style={{ '--tw-translate-y': '256px' }}></div>
                        </div>
                    </div>
                    <div className="order-1 md:order-2">
                        <div className="font-data text-[#7B61FF] mb-4 text-sm tracking-widest uppercase">Step 02</div>
                        <h2 className="text-[#F0EFF4] font-bold text-5xl md:text-6xl mb-6">We Harden<br /><span className="font-drama text-[#7B61FF] italic pr-2">the Perimeter.</span></h2>
                        <p className="text-[#F0EFF4]/60 text-lg leading-relaxed mb-4">
                            OpenClaw is installed and fortified with Tailscale end-to-end encryption and an invisible IP so you vanish off public scanners. We then deliberately disable SSH access — so once it ships, even we cannot log in.
                        </p>
                        <p className="text-[#F0EFF4]/40 text-sm font-data">
                            Your data. Your desk. Our hands off.
                        </p>
                    </div>
                </div>
            </div>

            {/* Panel 3 — Step 3: We Wire Your Business In */}
            <div className="protocol-panel h-[100dvh] w-full flex items-center justify-center p-6 relative bg-[#1D1D28] z-30 border-t border-[#F0EFF4]/5">
                <div className="max-w-4xl w-full mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="font-data text-[#7B61FF] mb-4 text-sm tracking-widest uppercase">Step 03</div>
                        <h2 className="text-[#F0EFF4] font-bold text-5xl md:text-6xl mb-6">We Wire<br /><span className="font-drama text-[#7B61FF] italic pr-2">Your Business In.</span></h2>
                        <p className="text-[#F0EFF4]/60 text-lg leading-relaxed mb-4">
                            We establish live API bridges to up to 3 of your software platforms — HubSpot, JustCall, Zuper, Sumo, or any tool with an open API. Then we sit with you on a 3-hour intensive call, load your SOPs into the agent, and teach you to command it.
                        </p>
                        <p className="text-[#F0EFF4]/40 text-sm font-data">
                            After this call, you will know more about AI than you have in the last 3 years. Guaranteed.
                        </p>
                    </div>
                    <div className="flex justify-center flex-col items-center">
                        <div className="relative w-64 h-32 flex items-center justify-center">
                            <svg viewBox="0 0 200 100" className="w-full h-full stroke-[#7B61FF] fill-none stroke-[2px]">
                                <path
                                    d="M0 50 L40 50 L50 20 L60 80 L70 50 L130 50 L140 10 L150 90 L160 50 L200 50"
                                    strokeDasharray="400"
                                    strokeDashoffset="400"
                                    className="animate-[dash_3s_linear_infinite]"
                                />
                                <style>{`
                                    @keyframes dash {
                                      to { stroke-dashoffset: 0; }
                                    }
                                `}</style>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

        </section>
    );
};

export default Protocol;
